import { neutral, warning, success, primary } from '_lex/_foundations/phxColors';

import type { ActiveBadgeColors, NextBadgeColors, CompactBadgeColors, DraftStatus } from './types';

export const STATUS_ORDER: DraftStatus[] = ['DRAFT', 'IN_APPROVAL', 'APPROVED', 'ISSUED'];

/** Active badge — solid fill, always expanded. */
export const ACTIVE_COLORS: Record<DraftStatus, ActiveBadgeColors> = {
  DRAFT:       { bg: neutral[100],    text: neutral[700],  border: neutral[800],  dashed: false },
  IN_APPROVAL: { bg: warning[50],    text: warning[800],  border: warning[800],  dashed: false },
  APPROVED:    { bg: success[50],     text: success[700],  border: success[700],  dashed: false },
  ISSUED:      { bg: 'transparent',   text: primary[700],  border: primary[700],  dashed: false },
};

/** Next badge — ghost/dashed resting state, with its own hover state. */
export const NEXT_COLORS: Record<DraftStatus, NextBadgeColors> = {
  DRAFT: {
    bg: 'transparent', text: neutral[800], border: neutral[800], dashed: true,
    hover: { bg: neutral[100], border: neutral[800], dashed: true },
  },
  IN_APPROVAL: {
    bg: warning[25], text: warning[800], border: warning[800], dashed: true,
    hover: { bg: warning[50], border: warning[800], dashed: true },
  },
  APPROVED: {
    bg: success[25], text: success[700], border: success[700], dashed: true,
    hover: { bg: success[50], border: success[700], dashed: true },
  },
  ISSUED: {
    bg: primary[25], text: primary[700], border: primary[700], dashed: true,
    hover: { bg: primary[50], border: primary[700], dashed: true },
  },
};

/** Complete badge — compact circle at rest (minimized), expanded label on hover (open). */
export const COMPLETE_COLORS: Record<DraftStatus, CompactBadgeColors> = {
  DRAFT: {
    minimized: { bg: neutral[50],  text: neutral[800],  border: neutral[600],  dashed: false },
    open:      { bg: neutral[100],  border: neutral[800],  dashed: true },
  },
  IN_APPROVAL: {
    minimized: { bg: warning[25],  text: warning[700],  border: warning[700],  dashed: false },
    open:      { bg: warning[25],  border: warning[800],  dashed: true },
  },
  APPROVED: {
    minimized: { bg: success[50],  text: success[500],  border: success[500],  dashed: false },
    open:      { bg: success[25],  border: success[500],  dashed: true },
  },
  ISSUED: {
    minimized: { bg: primary[500], text: primary[500],  border: primary[500],  dashed: false },
    open:      { bg: primary[50],  border: primary[500],  dashed: true },
  },
};

/** Pending badge — compact circle at rest (minimized), expanded label on hover (open). */
export const PENDING_COLORS: Record<DraftStatus, CompactBadgeColors> = {
  DRAFT: {
    minimized: { bg: neutral[200], text: neutral[600], border: 'transparent', dashed: false },
    open:      { bg: neutral[50],  border: neutral[300], dashed: true },
  },
  IN_APPROVAL: {
    minimized: { bg: neutral[200], text: neutral[600], border: 'transparent', dashed: false },
    open:      { bg: neutral[50],  border: neutral[300], dashed: true },
  },
  APPROVED: {
    minimized: { bg: neutral[100], text: neutral[600], border: neutral[200], dashed: false },
    open:      { bg: neutral[50],  border: neutral[300], dashed: false },
  },
  ISSUED: {
    minimized: { bg: neutral[100], text: neutral[600], border: neutral[200], dashed: false },
    open:      { bg: neutral[50],  border: neutral[300], dashed: false },
  },
};

/** Shared transition applied to every badge variant */
export const BADGE_TRANSITION =
  'background-color 200ms ease-in-out, border-color 200ms ease-in-out, transform 200ms ease-in-out, width 200ms ease-in-out, padding 200ms ease-in-out';

/** Shared sx properties for the compact (complete / pending) circular badge shell */
export const compactBadgeBaseSx = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 24,
  height: 24,
  borderRadius: 999,
  flexShrink: 0,
  overflow: 'hidden',
  transition: BADGE_TRANSITION,
} as const;

export const BADGE_FONT_SIZE_ACTIVE = 14;   // px — active badge label
export const BADGE_FONT_SIZE_DEFAULT = 12;  // px — all other badge labels (next, complete hover, pending hover)

/** Builds a CSS border string from a color entry. */
export function makeBorder(colorEntry: { border: string; dashed: boolean }): string {
  return `1px ${colorEntry.dashed ? 'dashed' : 'solid'} ${colorEntry.border}`;
}

/** Shared sx for the label Typography inside expanded compact badges */
export const expandedLabelSx = {
  fontSize: BADGE_FONT_SIZE_DEFAULT,
  fontWeight: 500,
  whiteSpace: 'nowrap',
  lineHeight: 1,
} as const;
